import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';

import PropTypes from 'prop-types';
import { throttle, times, flatten } from 'underscore';
import { isDesktop } from 'bv';

import { useWindowSize } from '../../hooks';
import Carousel from './carousel';

const CarouselContainer = React.forwardRef((props, ref) => {
  // state variables
  const [displayArrows, setDisplayArrows] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [isInfinite, setIsInfinite] = useState(false);
  const [originalListWidth, setOriginalListWidth] = useState();

  // refs
  const carouselWrapEl = useRef(null);
  const carouselEl = ref || useRef(null);

  // props
  const {
    scrollStep,
    activeRef,
    infinite,
    children,
    ...rest
  } = props;

  // callbacks
  const scroll = useCallback(
    (multiplier) => () => {
      const scrollAmount = carouselEl.current.scrollLeft + multiplier * scrollStep;

      if (carouselEl.current.scrollTo) {
        carouselEl.current.scrollTo({ left: scrollAmount, behavior: 'smooth' });
      } else {
        carouselEl.current.scrollLeft = scrollAmount;
      }
    },
    [carouselEl, scrollStep],
  );

  const setArrowsStatus = useCallback(
    () => {
      const element = carouselEl.current;

      setCanScrollLeft(element.scrollLeft > 0);

      if (isInfinite) return;
      setCanScrollRight(element.scrollLeft + element.clientWidth < element.scrollWidth);
    },
    [carouselEl, setCanScrollLeft, setCanScrollRight, isInfinite],
  );

  // The idea of this method is to move the scroll to a position in which the same content
  // Is shown, giving the feeling that the list never ends, but actually we are positioning
  // The scroll to the right or to the left when we scroll to the left/right
  const adjustInfiniteScroll = useCallback(
    () => {
      const element = carouselEl.current;

      if (element.scrollLeft > originalListWidth) {
        element.scrollLeft -= originalListWidth;
      }
    },
    [carouselEl, originalListWidth],
  );

  // Check if infinite scrolling should be applied or not
  const initInfinite = useCallback(
    () => {
      const element = carouselEl.current;
      setIsInfinite(true);

      // Padding right is not considered because it's a flex without after content
      const { paddingLeft } = getComputedStyle(element);
      const padding = parseInt(paddingLeft, 10);

      setOriginalListWidth(element.scrollWidth - padding);
    },
    [],
  );

  // On scroll we want to:
  // set the arrow status (check if end is reached) (infinite: false)
  // adjust the scroll to simulate the endless list (infinite: true)
  const onScroll = useCallback(
    throttle(() => {
      if (infinite && !isInfinite) initInfinite();
      if (isInfinite) adjustInfiniteScroll();
      if (displayArrows) setArrowsStatus();
    }, 100),
    [infinite, displayArrows, infinite, setArrowsStatus, adjustInfiniteScroll],
  );

  const { innerWidth } = useWindowSize();

  // effects
  if (isDesktop()) {
    const arrowsFn = () => {
      if (carouselWrapEl.current.clientWidth < carouselEl.current.scrollWidth) {
        setDisplayArrows(true);
        setArrowsStatus();
      } else {
        setDisplayArrows(false);
      }
    };

    useEffect(arrowsFn, [children, props.dangerouslySetInnerHTML, innerWidth]);
  }

  useEffect(() => {
    if (activeRef && activeRef.current) {
      const element = carouselEl.current;

      element.scrollLeft = (
        activeRef.current.offsetLeft
        - element.offsetLeft
        - (element.clientWidth / 2)
        + (activeRef.current.offsetWidth / 2)
      );
    }
  }, []);

  // render
  return (
    <Carousel
      canScrollLeft={canScrollLeft}
      canScrollRight={canScrollRight || infinite}
      carouselWrapElRef={carouselWrapEl}
      carouselElRef={carouselEl}
      displayArrows={displayArrows}
      onScroll={onScroll}
      scroll={scroll}
      {...rest}
    >
      {isInfinite ? flatten(times(2, () => children)) : children}
    </Carousel>
  );
});

CarouselContainer.propTypes = {
  scrollStep: PropTypes.number.isRequired,
  activeRef: PropTypes.node,
  children: PropTypes.node.isRequired,
  dangerouslySetInnerHTML: PropTypes.instanceOf(Object),
  infinite: PropTypes.bool,
};

CarouselContainer.defaultProps = {
  activeRef: null,
  dangerouslySetInnerHTML: null,
  infinite: false,
};

export default CarouselContainer;
